import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Center, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { Wrench } from 'lucide-react';

import { getCoreVersionInfo } from '@/lib/api/core';
import { formatUntil, setMaintenance, useMaintenance, type MaintenanceInfo } from '@/lib/maintenance';

const MaintenancePage: React.FC<{ info: MaintenanceInfo }> = ({ info }) => {
  // The version endpoint is exempt from maintenance: poll it to know when it is over.
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['maintenance-recheck'],
    queryFn: ({ signal }) => getCoreVersionInfo(signal),
    refetchInterval: 30_000,
  });
  React.useEffect(() => {
    if (data?.maintenance && !data.maintenance.enabled) {
      setMaintenance(null);
      window.location.reload();
    }
  }, [data?.maintenance]);

  const until = formatUntil(info.until);
  return (
    <Center mih="100vh" p="md" bg="var(--mantine-color-body)">
      <Paper withBorder radius="md" p="xl" maw={480} w="100%">
        <Stack align="center" gap="md" ta="center">
          <ThemeIcon size={56} radius="xl" variant="light" color="blue">
            <Wrench size={28} />
          </ThemeIcon>
          <Title order={2}>Maintenance</Title>
          <Text>{info.message || 'nodus is being updated and will be back in a few minutes.'}</Text>
          {until && <Text c="dimmed" fz="sm">Expected back at {until}.</Text>}
          <Button variant="light" loading={isFetching} onClick={() => refetch()}>
            Try again
          </Button>
        </Stack>
      </Paper>
    </Center>
  );
};

/**
 * Renders the full-page maintenance screen instead of the app once the API
 * has answered 503 maintenance (see ``lib/maintenance.ts``); otherwise the app.
 */
export const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const info = useMaintenance();
  if (info) return <MaintenancePage info={info} />;
  return <>{children}</>;
};
